import { observer } from 'src/utils/mobx-react';

import {
    OverlayDefault,
    PopupHeader,
    PopupDescription,
    AcceptBtn,
    MarkedTxt,
    ContentRingFencedFunds,
    LinkWrapper,
} from 'src/domains/players/webview/components/Account/Notifications/BlockingNotifications.style';
import React from 'react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { UniverseComponent } from 'src/domains/common/universes';
import { useCommon } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

interface BlockingRingfencedFundsNotificationPropsType {
    onConfirm: () => Promise<void>;
}

export const BlockingRingedfencedFundsNotification = observer(
    'BlockingRingedfencedFundsNotification',
    ({ onConfirm }: BlockingRingfencedFundsNotificationPropsType): JSX.Element | null => {
        const common = useCommon();
        const { accountHelperMail } = ConfigComponents.get(common).config;
        const languagesState = LanguagesState.get(common);

        const markedTxt1 = languagesState.getTranslation(
            'account.ringfenced-funds.description-marked-1',
            'licence conditions detailing what we need to tell customers about their funds.'
        );
        const markedTxt2 = languagesState.getTranslation(
            'account.blocking-popup.ringfenced-funds.additional-description-marked-2',
            'the customer funds insolvency ratings system.'
        );

        return (
            <OverlayDefault data-test='overlay-prompt'>
                <ContentRingFencedFunds>
                    <UniverseComponent>
                        {(universeModule): React.ReactElement => <universeModule.compliancePopupLogo />}
                    </UniverseComponent>

                    <PopupHeader>
                        <I18n
                            langKey='account.blocking-popup.ringfenced-funds-notification.title'
                            defaultText='Ringfenced funds'
                        />
                    </PopupHeader>
                    <ContentRingFencedFunds>
                        <PopupDescription data-test='account.blocking-popup.ringfenced-funds-description-line1'>
                            <I18n
                                langKey='account.blocking-popup.ringfenced-funds.description-line1'
                                defaultText='We are required by our licence to inform customers about what happens to funds which we hold on account for you in the event of insolvency. The Gambling Commission provides further information on our '
                            />
                            <MarkedTxt
                                href='https://www.gamblingcommission.gov.uk/guidance/customer-funds-segregation-disclosure-to-customers-and-reporting/advice-on-implementing-licence-condition-4-2-1-disclosure-to-customers'
                                target='_blank'
                                rel='noopener'
                                data-test='ringfenced-funds-disclosure-to-customers'
                            >
                                {markedTxt1}
                            </MarkedTxt>
                        </PopupDescription>

                        <PopupDescription data-test='account.blocking-popup.ringfenced-funds-description-line2'>
                            <I18n
                                langKey='account.blocking-popup.ringfenced-funds.description-line2'
                                defaultText='We hold customer funds separate from company funds. These funds are not protected in the event of insolvency: not protected segregation. For more information, please see '
                            />
                            <MarkedTxt
                                href='https://www.gamblingcommission.gov.uk/guidance/customer-funds-segregation-disclosure-to-customers-and-reporting/the-customer-funds-insolvency-ratings-system'
                                target='_blank'
                                rel='noopener'
                                data-test='account.blocking-popup.ringfenced-funds-insolvency-ratings-system'
                            >
                                {markedTxt2}
                            </MarkedTxt>
                        </PopupDescription>

                        <PopupDescription>
                            <I18n
                                langKey='account.blocking-popup.ringfenced-funds.not-agree-mail'
                                defaultText='If you do not agree with this, do not click the accept button and contact'
                            />{' '}
                            <LinkWrapper to={`mailto:${accountHelperMail}`}>
                                <I18n
                                    langKey='account.change-ringfenced-funds-notification.email'
                                    defaultText='{accountHelperMail}'
                                    params={{ accountHelperMail }}
                                />
                            </LinkWrapper>
                        </PopupDescription>
                    </ContentRingFencedFunds>
                    <AcceptBtn
                        onClick={onConfirm}
                        size='medium'
                    >
                        <I18n
                            langKey='account.blocking-popup.confirm-button.label'
                            defaultText='Accept'
                        />
                    </AcceptBtn>
                </ContentRingFencedFunds>
            </OverlayDefault>
        );
    }
);
